import type { DerivedStaypoint, DerivedTimelineItem, DerivedTripleg } from './timelineTypes';

const normalizeMeasuredAt = (value?: string | null) => {
  if (!value) {
    return null;
  }
  return value
    .trim()
    .replace(/([+-]\d{2}:\d{2})Z$/, '$1')
    .replace(' ', 'T');
};

const parseMeasuredAtMs = (value?: string | null) => {
  const normalized = normalizeMeasuredAt(value);
  if (!normalized) {
    return 0;
  }
  const epochMs = Date.parse(normalized);
  return Number.isNaN(epochMs) ? 0 : epochMs;
};

export const buildLocationAnalysisTimelineItems = (
  staypoints: DerivedStaypoint[],
  triplegs: DerivedTripleg[]
): DerivedTimelineItem[] => {
  const staypointItems = staypoints.map((staypoint, index) => ({
    type: 'staypoint' as const,
    id: staypoint.id,
    started_at: staypoint.started_at,
    ended_at: staypoint.ended_at,
    staypoint,
    displayIndex: index + 1,
  }));
  const triplegItems = triplegs.map((tripleg, index) => ({
    type: 'tripleg' as const,
    id: tripleg.id,
    started_at: tripleg.started_at,
    ended_at: tripleg.ended_at,
    tripleg,
    displayIndex: index + 1,
  }));

  const sorted = [...staypointItems, ...triplegItems].sort((left, right) => {
    const leftTime = parseMeasuredAtMs(left.started_at);
    const rightTime = parseMeasuredAtMs(right.started_at);
    if (leftTime !== rightTime) {
      return leftTime - rightTime;
    }
    if (left.type === right.type) {
      return 0;
    }
    return left.type === 'staypoint' ? -1 : 1;
  });

  return sorted.reduce<DerivedTimelineItem[]>((accumulator, item) => {
    const previous = accumulator[accumulator.length - 1];
    if (
      previous?.type === 'staypoint' &&
      item.type === 'staypoint' &&
      previous.staypoint.effective_location_id &&
      previous.staypoint.effective_location_id === item.staypoint.effective_location_id
    ) {
      accumulator[accumulator.length - 1] = {
        ...previous,
        ended_at: item.ended_at,
        staypoint: {
          ...previous.staypoint,
          ended_at: item.ended_at,
          source_snapshot_count: previous.staypoint.source_snapshot_count + item.staypoint.source_snapshot_count,
        },
      };
      return accumulator;
    }
    accumulator.push(item);
    return accumulator;
  }, []);
};
